import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Search, PlayCircle, ClipboardList, AlertTriangle, CheckCircle2, RotateCcw, Eye, FileWarning, Ban, ShieldCheck, Zap } from "lucide-react";

const MESES_ES = ["", "Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const labelPeriodo = (p) => {
  if (!p || !/^\d{4}-\d{2}$/.test(p)) return p || "-";
  const [y, m] = p.split("-");
  return `${MESES_ES[Number(m)] || m} ${y}`;
};

export default function MantenimientoPage() {
  const { token, user } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [logs, setLogs] = useState([]);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detalleLog, setDetalleLog] = useState(null);

  // Saneamiento de planillas duplicadas
  const [dupResult, setDupResult] = useState(null); // { total_a_anular, grupos, ... }
  const [dupLoading, setDupLoading] = useState(false);
  const [dupDetalle, setDupDetalle] = useState(null); // grupo seleccionado

  // Sanity check permisos
  const [permResult, setPermResult] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permDetalle, setPermDetalle] = useState(null);

  // Reconciliación de créditos fiscales (excedentes de pago sin crédito materializado)
  const [credResult, setCredResult] = useState(null);
  const [credLoading, setCredLoading] = useState(false);

  // Limpieza de liquidaciones legacy (colección obsoleta `liquidaciones`)
  const [legacyResult, setLegacyResult] = useState(null);       // resultado del detectar
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyElimResult, setLegacyElimResult] = useState(null); // resultado del eliminar
  const [legacyElimDias, setLegacyElimDias] = useState(30);

  const loadLogs = () => axios.get(`${API}/mantenimiento/logs`, { ...auth, params: { limit: 50 } })
    .then(r => setLogs(r.data)).catch(() => {});
  useEffect(() => { loadLogs(); }, []); // eslint-disable-line

  const ejecutar = async (dry) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/detectar-huerfanas`, null, { ...auth, params: { dry_run: dry } });
      setUltimoResultado(r.data);
      const msg = dry
        ? `Detección: ${r.data.encontradas} huérfana(s) encontradas`
        : `${r.data.revertidas} declaración(es) revertidas a Registrada`;
      toast.success(msg);
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar mantenimiento");
    } finally { setLoading(false); }
  };

  const verDetalle = async (id) => {
    try {
      const r = await axios.get(`${API}/mantenimiento/logs/${id}`, auth);
      setDetalleLog(r.data);
    } catch { toast.error("No se pudo cargar el detalle"); }
  };

  // === Saneamiento de planillas duplicadas ===
  const escanearDuplicadas = async () => {
    setDupLoading(true);
    try {
      const r = await axios.get(`${API}/mantenimiento/planillas-duplicadas`, auth);
      setDupResult(r.data);
      const n = r.data?.total_a_anular || 0;
      if (n === 0) {
        toast.success("Sin duplicados — la base de datos está saneada");
      } else {
        toast.success(`${n} planilla(s) duplicada(s) detectada(s) en ${r.data?.total_grupos_conflicto || 0} contribuyente(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al escanear duplicadas");
    } finally { setDupLoading(false); }
  };

  const ejecutarSaneamiento = async () => {
    if (!dupResult?.total_a_anular) return;
    if (!window.confirm(
      `¿Confirma anular ${dupResult.total_a_anular} planilla(s) duplicada(s)?\n\n` +
      `Solo se anulan las duplicadas (Registradas). Las Pagadas y las más recientes se conservan. ` +
      `La acción queda registrada en el log y en auditoría.`
    )) return;
    setDupLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/planillas-duplicadas/sanear?dry_run=false`, null, auth);
      toast.success(`${r.data?.anuladas || 0} planilla(s) anulada(s) exitosamente`);
      setDupResult(r.data);
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar saneamiento");
    } finally { setDupLoading(false); }
  };

  // === Sanity check permisos ===
  const escanearPermisos = async () => {
    setPermLoading(true);
    try {
      const r = await axios.get(`${API}/mantenimiento/sanity-permisos`, auth);
      setPermResult(r.data);
      const n = r.data?.roles_con_incoherencias || 0;
      if (n === 0) {
        toast.success(`Todos los ${r.data?.total_roles || 0} roles son coherentes con las ${r.data?.reglas_aplicadas || 0} reglas aplicadas`);
      } else {
        toast.warning(`${n} rol(es) con incoherencias: ${r.data?.total_findings || 0} permiso(s) faltante(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al escanear permisos");
    } finally { setPermLoading(false); }
  };

  const aplicarPermisosFaltantes = async (roleId, roleName) => {
    if (!window.confirm(`¿Confirma agregar los permisos faltantes al rol "${roleName}"?\n\nSolo se AGREGAN los permisos necesarios; no se quita nada.`)) return;
    setPermLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/sanity-permisos/aplicar/${roleId}`, null, auth);
      toast.success(r.data?.message || "Permisos aplicados");
      // Re-scan
      await escanearPermisos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aplicar permisos");
    } finally { setPermLoading(false); }
  };

  // === Reconciliación de créditos fiscales ===
  const escanearCreditos = async () => {
    setCredLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/reconciliar-creditos-fiscales?dry_run=true`, null, auth);
      setCredResult(r.data);
      const n = r.data?.faltantes || 0;
      if (n === 0) toast.success("Todos los pagos con excedente tienen su crédito fiscal materializado ✓");
      else toast.warning(`${n} pago(s) con excedente detectado(s) sin crédito fiscal materializado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al escanear créditos");
    } finally { setCredLoading(false); }
  };

  const aplicarCreditos = async () => {
    if (!credResult?.faltantes) return;
    if (!window.confirm(`¿Confirma crear ${credResult.faltantes} crédito(s) fiscal(es) faltante(s)?\n\nEsta operación es idempotente: NO duplica créditos ya existentes.`)) return;
    setCredLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/reconciliar-creditos-fiscales?dry_run=false`, null, auth);
      toast.success(`${r.data?.faltantes || 0} crédito(s) fiscal(es) creado(s) exitosamente`);
      setCredResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aplicar créditos");
    } finally { setCredLoading(false); }
  };

  // === Limpieza de liquidaciones legacy ===
  const escanearLegacy = async () => {
    setLegacyLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/legacy-liquidaciones/detectar?dry_run=true`, null, auth);
      setLegacyResult(r.data);
      const n = r.data?.encontradas || 0;
      if (n === 0) toast.success("No hay liquidaciones legacy huérfanas ✓");
      else toast.warning(`${n} liquidación(es) legacy huérfana(s) detectada(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al escanear legacy");
    } finally { setLegacyLoading(false); }
  };

  const marcarLegacy = async () => {
    if (!legacyResult?.encontradas) return;
    if (!window.confirm(
      `¿Confirma marcar ${legacyResult.encontradas} liquidación(es) como is_legacy=true?\n\n` +
      `Las marcadas quedarán ocultas del listado normal. Es idempotente y NO elimina físicamente.\n` +
      `Después podrás eliminarlas definitivamente tras N días de margen.`
    )) return;
    setLegacyLoading(true);
    try {
      const r = await axios.post(`${API}/mantenimiento/legacy-liquidaciones/detectar?dry_run=false`, null, auth);
      toast.success(`${r.data?.marcadas || 0} liquidación(es) legacy marcada(s) correctamente`);
      setLegacyResult(r.data);
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al marcar legacy");
    } finally { setLegacyLoading(false); }
  };

  const eliminarLegacy = async (dryRun = true) => {
    if (!dryRun && !window.confirm(
      `⚠️ ELIMINACIÓN DEFINITIVA\n\n` +
      `¿Confirma eliminar físicamente las liquidaciones marcadas como is_legacy=true con al menos ` +
      `${legacyElimDias} días desde el marcado?\n\n` +
      `Esta operación NO se puede deshacer.`
    )) return;
    setLegacyLoading(true);
    try {
      const r = await axios.post(
        `${API}/mantenimiento/legacy-liquidaciones/eliminar?dias_desde_marcado=${legacyElimDias}&dry_run=${dryRun}`,
        null, auth
      );
      setLegacyElimResult(r.data);
      if (dryRun) {
        toast.info(`Simulación: ${r.data?.candidatas || 0} liquidación(es) elegible(s) para eliminar`);
      } else {
        toast.success(`${r.data?.eliminadas || 0} liquidación(es) eliminada(s) definitivamente`);
        loadLogs();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar legacy");
    } finally { setLegacyLoading(false); }
  };



  const disparadorBadge = (v) => {
    if (v === "scheduler") return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Automático</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Manual</Badge>;
  };

  if (user?.role !== "superadmin" && user?.role !== "admin_financiero") {
    return (
      <Card className="p-8 text-center border-orange-200 bg-orange-50">
        <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-3" />
        <p className="font-semibold text-orange-900">Acceso restringido</p>
        <p className="text-sm text-orange-800/80 mt-1">Solo administradores pueden acceder al módulo de mantenimiento.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="mantenimiento-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
          <Wrench className="w-8 h-8 text-orange-500" /> Mantenimiento del sistema
        </h1>
        <p className="text-green-700/70 mt-1">
          Detección y reparación automática de datos inconsistentes. El sistema ejecuta este proceso
          <strong className="text-green-900"> automáticamente cada 24 horas</strong>, pero puede dispararlo manualmente cuando lo requiera.
        </p>
      </div>

      {/* Acciones */}
      <Card className="p-6 border-green-100">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Detectar (dry run) */}
          <div className="p-5 rounded-2xl bg-blue-50/50 border border-blue-100">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <Search className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="font-display font-bold text-blue-900 text-lg">Solo detectar</h3>
                <p className="text-sm text-blue-800/80 mt-0.5">Escanea la BD y reporta declaraciones huérfanas sin modificarlas.</p>
              </div>
            </div>
            <Button
              onClick={() => ejecutar(true)}
              disabled={loading}
              variant="outline"
              className="w-full border-blue-300 text-blue-800 hover:bg-blue-100"
              data-testid="btn-detectar-dry-run"
            >
              <Search className="w-4 h-4 mr-2" />
              {loading ? "Escaneando..." : "Ejecutar detección"}
            </Button>
          </div>

          {/* Ejecutar reparación */}
          <div className="p-5 rounded-2xl bg-orange-50/60 border border-orange-100">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                <PlayCircle className="w-6 h-6 text-orange-700" />
              </div>
              <div>
                <h3 className="font-display font-bold text-orange-900 text-lg">Detectar y reparar</h3>
                <p className="text-sm text-orange-800/80 mt-0.5">Revierte a &quot;Registrada&quot; las declaraciones huérfanas detectadas.</p>
              </div>
            </div>
            <Button
              onClick={() => {
                if (window.confirm("¿Está seguro? Esto revertirá a 'Registrada' todas las declaraciones huérfanas encontradas. La acción queda registrada en el log.")) ejecutar(false);
              }}
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="btn-ejecutar-reparacion"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {loading ? "Reparando..." : "Ejecutar reparación"}
            </Button>
          </div>
        </div>

        {/* Último resultado */}
        {ultimoResultado && (
          <div className="mt-5 p-4 rounded-lg bg-green-50/60 border border-green-100" data-testid="ultimo-resultado">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-semibold text-green-900">Última ejecución completada</p>
                  <p className="text-sm text-green-800/80">
                    Encontradas: <strong>{ultimoResultado.encontradas}</strong> · Revertidas: <strong>{ultimoResultado.revertidas}</strong> · {ultimoResultado.dry_run ? "modo detección" : "reparadas"}
                  </p>
                </div>
              </div>
              {ultimoResultado.acciones?.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setDetalleLog(ultimoResultado)}>
                  <Eye className="w-4 h-4 mr-1" /> Ver detalle
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Saneamiento de planillas duplicadas */}
      <Card className="p-6 border-amber-100 bg-gradient-to-br from-amber-50/40 to-orange-50/30" data-testid="section-duplicadas">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
            <FileWarning className="w-6 h-6 text-amber-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-amber-900 text-lg">Planillas duplicadas por período</h3>
            <p className="text-sm text-amber-800/80 mt-0.5">
              Detecta contribuyentes con múltiples planillas <strong>Registradas</strong> cubriendo el mismo mes.
              Conserva las <strong>Pagadas</strong> y la más reciente por grupo; anula el resto.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={escanearDuplicadas}
            disabled={dupLoading}
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-100"
            data-testid="btn-escanear-duplicadas"
          >
            <Search className="w-4 h-4 mr-2" />
            {dupLoading ? "Escaneando..." : "Escanear duplicadas"}
          </Button>
          {dupResult && dupResult.total_a_anular > 0 && (
            <Button
              onClick={ejecutarSaneamiento}
              disabled={dupLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-ejecutar-saneamiento-duplicadas"
            >
              <Ban className="w-4 h-4 mr-2" />
              Anular {dupResult.total_a_anular} planilla(s) duplicada(s)
            </Button>
          )}
        </div>

        {/* Resultados */}
        {dupResult && (
          <div className="mt-5 space-y-3">
            <div className="p-4 rounded-lg bg-white border border-amber-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Analizadas</p>
                  <p className="font-bold text-slate-900 text-lg">{dupResult.total_planillas_analizadas}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Contribuyentes</p>
                  <p className="font-bold text-slate-900 text-lg">{dupResult.total_contribuyentes}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Con duplicados</p>
                  <p className="font-bold text-amber-700 text-lg">{dupResult.total_grupos_conflicto}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">A anular</p>
                  <p className="font-bold text-red-700 text-lg">{dupResult.total_a_anular}</p>
                </div>
              </div>
              {dupResult.anuladas > 0 && (
                <div className="mt-3 p-2.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Saneamiento ejecutado: <strong>{dupResult.anuladas}</strong> planilla(s) anulada(s) correctamente.
                </div>
              )}
            </div>

            {dupResult.grupos && dupResult.grupos.length > 0 && (
              <div className="overflow-x-auto border border-amber-100 rounded-lg bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50 text-amber-900">
                    <tr>
                      <th className="text-left p-2.5">Contribuyente</th>
                      <th className="text-left p-2.5">Documento</th>
                      <th className="text-center p-2.5">Conservada</th>
                      <th className="text-center p-2.5">A anular</th>
                      <th className="text-right p-2.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-50">
                    {dupResult.grupos.map(g => (
                      <tr key={g.contribuyente_id} data-testid={`dup-row-${g.contribuyente_id}`}>
                        <td className="p-2.5 font-medium text-slate-900">{g.contribuyente_nombre}</td>
                        <td className="p-2.5 text-slate-700 font-mono text-xs">{g.documento}</td>
                        <td className="p-2.5 text-center">
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            {g.planillas_conservadas.length}
                          </Badge>
                        </td>
                        <td className="p-2.5 text-center">
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                            {g.planillas_a_anular.length}
                          </Badge>
                        </td>
                        <td className="p-2.5 text-right">
                          <Button size="sm" variant="ghost" onClick={() => setDupDetalle(g)}>
                            <Eye className="w-4 h-4 mr-1" /> Detalle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Sanity check permisos por rol */}
      <Card className="p-6 border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-blue-50/30" data-testid="section-sanity-permisos">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-indigo-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-indigo-900 text-lg">Sanity check de permisos por rol</h3>
            <p className="text-sm text-indigo-800/80 mt-0.5">
              Analiza cada rol contra <strong>reglas de coherencia</strong> (por ejemplo: si un rol tiene
              <code className="mx-1 px-1.5 py-0.5 rounded bg-white text-indigo-800 text-xs">contribuyentes.edit</code>
              debe tener también <code className="mx-1 px-1.5 py-0.5 rounded bg-white text-indigo-800 text-xs">definiciones.view</code>).
              Detecta permisos faltantes y ofrece aplicarlos automáticamente.
            </p>
          </div>
        </div>

        <Button
          onClick={escanearPermisos}
          disabled={permLoading}
          variant="outline"
          className="border-indigo-400 text-indigo-800 hover:bg-indigo-100"
          data-testid="btn-sanity-permisos"
        >
          <Search className="w-4 h-4 mr-2" />
          {permLoading ? "Escaneando..." : "Escanear coherencia de permisos"}
        </Button>

        {permResult && (
          <div className="mt-5 space-y-3">
            <div className="p-4 rounded-lg bg-white border border-indigo-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Roles analizados</p>
                  <p className="font-bold text-slate-900 text-lg">{permResult.total_roles}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Reglas aplicadas</p>
                  <p className="font-bold text-slate-900 text-lg">{permResult.reglas_aplicadas}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Con incoherencias</p>
                  <p className={`font-bold text-lg ${permResult.roles_con_incoherencias > 0 ? "text-amber-700" : "text-emerald-700"}`}>{permResult.roles_con_incoherencias}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Permisos faltantes</p>
                  <p className={`font-bold text-lg ${permResult.total_findings > 0 ? "text-red-700" : "text-emerald-700"}`}>{permResult.total_findings}</p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto border border-indigo-100 rounded-lg bg-white">
              <table className="w-full text-sm">
                <thead className="bg-indigo-50 text-indigo-900">
                  <tr>
                    <th className="text-left p-2.5">Rol</th>
                    <th className="text-center p-2.5">Usuarios</th>
                    <th className="text-center p-2.5">Permisos</th>
                    <th className="text-center p-2.5">Findings</th>
                    <th className="text-center p-2.5">Severidad</th>
                    <th className="text-right p-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-indigo-50">
                  {permResult.resultados.map(r => (
                    <tr key={r.id} data-testid={`sanity-row-${r.id}`}>
                      <td className="p-2.5 font-medium text-slate-900">{r.nombre}</td>
                      <td className="p-2.5 text-center text-slate-700">{r.usuarios_activos}</td>
                      <td className="p-2.5 text-center text-slate-700 font-mono text-xs">{r.total_permisos}</td>
                      <td className="p-2.5 text-center">
                        {r.findings.length > 0 ? (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{r.findings.length}</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">0</Badge>
                        )}
                      </td>
                      <td className="p-2.5 text-center">
                        {r.severidad === "ok" && <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />OK</span>}
                        {r.severidad === "alta" && <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold"><AlertTriangle className="w-3.5 h-3.5" />Alta</span>}
                        {r.severidad === "critica" && <span className="inline-flex items-center gap-1 text-xs text-red-700 font-semibold"><AlertTriangle className="w-3.5 h-3.5" />Crítica</span>}
                      </td>
                      <td className="p-2.5 text-right whitespace-nowrap">
                        {r.findings.length > 0 && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setPermDetalle(r)}>
                              <Eye className="w-4 h-4 mr-1" /> Detalle
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => aplicarPermisosFaltantes(r.id, r.nombre)}
                              disabled={permLoading}
                              className="ml-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                              data-testid={`btn-aplicar-${r.id}`}
                            >
                              <Zap className="w-4 h-4 mr-1" /> Aplicar
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {/* === Reconciliación de créditos fiscales === */}
      <Card className="p-6 border-emerald-100 bg-gradient-to-br from-emerald-50/40 to-teal-50/30" data-testid="section-creditos-fiscales">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="font-display font-bold text-lg text-emerald-900 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-emerald-600" /> Reconciliación de créditos fiscales
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Detecta pagos <b>validados con excedente</b> que aún no tienen su crédito fiscal materializado
              en la colección <code className="text-xs bg-emerald-100 px-1 rounded">creditos_fiscales</code>.
              La operación es idempotente y segura (no duplica créditos existentes).
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={escanearCreditos}
              disabled={credLoading}
              className="border-emerald-300 text-emerald-800"
              data-testid="btn-escanear-creditos"
            >
              {credLoading ? "Escaneando…" : "Escanear"}
            </Button>
            <Button
              onClick={aplicarCreditos}
              disabled={credLoading || !credResult?.faltantes}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-aplicar-creditos"
            >
              {credLoading ? "Aplicando…" : `Crear ${credResult?.faltantes || 0} crédito(s)`}
            </Button>
          </div>
        </div>

        {credResult && (
          <div className="mt-2">
            {credResult.faltantes === 0 ? (
              <div className="p-3 bg-emerald-100 border border-emerald-200 rounded-lg text-emerald-900 text-sm" data-testid="creditos-ok">
                ✓ Todos los pagos con excedente tienen su crédito fiscal materializado.
              </div>
            ) : (
              <div className="overflow-x-auto border border-emerald-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-emerald-100 text-emerald-900">
                    <tr>
                      <th className="text-left p-2.5">Nº Comprobante</th>
                      <th className="text-left p-2.5">Contribuyente</th>
                      <th className="text-left p-2.5">Fecha</th>
                      <th className="text-right p-2.5">Excedente (Bs.)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {(credResult.detalle || []).map((d, i) => (
                      <tr key={i} className="border-t border-emerald-50" data-testid={`credito-fila-${i}`}>
                        <td className="p-2.5 font-mono">{d.nro_comprobante || "—"}</td>
                        <td className="p-2.5 text-xs font-mono">{d.contribuyente_id?.slice(0, 12) || "—"}…</td>
                        <td className="p-2.5">{d.fecha || "—"}</td>
                        <td className="p-2.5 text-right font-mono font-bold text-emerald-800">
                          {(Number(d.monto) || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-emerald-50 font-semibold">
                      <td className="p-2.5" colSpan={3}>Total</td>
                      <td className="p-2.5 text-right font-mono text-emerald-900">
                        {(credResult.detalle || []).reduce((a, x) => a + (Number(x.monto) || 0), 0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {credResult.aplicado && (
              <p className="text-[11px] text-emerald-800 mt-2 italic">✔ Créditos aplicados en la última ejecución.</p>
            )}
          </div>
        )}
      </Card>

      {/* === Limpieza de Liquidaciones Legacy === */}
      <Card className="p-6 border-slate-200 bg-gradient-to-br from-slate-50/60 to-gray-50/40" data-testid="section-legacy-liquidaciones">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-slate-600" /> Limpieza de liquidaciones legacy
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Detecta y limpia registros huérfanos de la colección <code className="text-xs bg-slate-200 px-1 rounded">liquidaciones</code> (antigua).
              Se marcan como <b>legacy</b> aquellas con estado <i>pendiente/anulada</i> que <b>NO</b> están referenciadas
              por ningún pago ni por ninguna declaración nueva (<code className="text-xs bg-slate-200 px-1 rounded">datos_declaracion</code>).
              Las <b>pagadas</b> nunca se tocan (histórico intocable).
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={escanearLegacy}
              disabled={legacyLoading}
              className="border-slate-300 text-slate-800"
              data-testid="btn-escanear-legacy"
            >
              {legacyLoading ? "Escaneando…" : "Escanear"}
            </Button>
            <Button
              onClick={marcarLegacy}
              disabled={legacyLoading || !legacyResult?.encontradas}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="btn-marcar-legacy"
            >
              {legacyLoading ? "Marcando…" : `Marcar ${legacyResult?.encontradas || 0} como legacy`}
            </Button>
          </div>
        </div>

        {legacyResult && (
          <div className="mt-2">
            {legacyResult.encontradas === 0 ? (
              <div className="p-3 bg-green-100 border border-green-200 rounded-lg text-green-900 text-sm" data-testid="legacy-ok">
                ✓ No hay liquidaciones legacy huérfanas en este momento.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                  <div className="p-2.5 rounded bg-white border border-slate-200">
                    <div className="text-xs text-slate-500">Encontradas</div>
                    <div className="text-2xl font-bold text-slate-900" data-testid="legacy-encontradas">{legacyResult.encontradas}</div>
                  </div>
                  <div className="p-2.5 rounded bg-white border border-slate-200">
                    <div className="text-xs text-slate-500">Estado: pendiente</div>
                    <div className="text-xl font-semibold text-orange-700">{legacyResult.candidatas_estado_pendiente || 0}</div>
                  </div>
                  <div className="p-2.5 rounded bg-white border border-slate-200">
                    <div className="text-xs text-slate-500">Estado: anulada</div>
                    <div className="text-xl font-semibold text-red-700">{legacyResult.candidatas_estado_anulada || 0}</div>
                  </div>
                  <div className="p-2.5 rounded bg-white border border-slate-200">
                    <div className="text-xs text-slate-500">Marcadas ahora</div>
                    <div className="text-xl font-semibold text-green-700">{legacyResult.marcadas || 0}</div>
                  </div>
                </div>
                <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-800 sticky top-0">
                      <tr>
                        <th className="text-left p-2.5">Nº Planilla</th>
                        <th className="text-left p-2.5">Contribuyente</th>
                        <th className="text-center p-2.5">Estado</th>
                        <th className="text-center p-2.5">Período</th>
                        <th className="text-right p-2.5">Monto (Bs.)</th>
                        <th className="text-left p-2.5">Creada</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {(legacyResult.detalle || []).slice(0, 100).map((d, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`legacy-fila-${i}`}>
                          <td className="p-2.5 font-mono text-xs">{d.nro_planilla || "—"}</td>
                          <td className="p-2.5 text-xs font-mono">{(d.contribuyente_id || "").slice(0, 12)}…</td>
                          <td className="p-2.5 text-center">
                            <Badge variant="outline" className={d.estado === "anulada" ? "border-red-300 text-red-700" : "border-orange-300 text-orange-700"}>
                              {d.estado}
                            </Badge>
                          </td>
                          <td className="p-2.5 text-center">{labelPeriodo(d.periodo)}</td>
                          <td className="p-2.5 text-right font-mono">{(Number(d.monto) || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}</td>
                          <td className="p-2.5 text-xs text-slate-600">{d.created_at ? new Date(d.created_at).toLocaleDateString("es-VE", { timeZone: "America/Caracas" }) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(legacyResult.detalle || []).length > 100 && (
                    <div className="p-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 text-center">
                      Mostrando primeras 100 de {legacyResult.detalle.length} — consulta los logs para el listado completo.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Eliminación diferida */}
            <div className="mt-4 p-4 rounded-lg bg-red-50/50 border border-red-200">
              <h4 className="font-semibold text-red-900 flex items-center gap-2 mb-2">
                <Ban className="w-4 h-4" /> Eliminación definitiva (opcional)
              </h4>
              <p className="text-xs text-red-800 mb-3">
                Elimina físicamente las liquidaciones ya marcadas <code>is_legacy=true</code> con al menos N días desde el marcado.
                Recomendado dejar un margen de 30 días de gracia para poder revertir.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm text-red-900">
                  Días desde marcado:{" "}
                  <input
                    type="number" min="0" max="3650"
                    value={legacyElimDias}
                    onChange={(e) => setLegacyElimDias(Number(e.target.value) || 0)}
                    className="w-20 px-2 py-1 border border-red-300 rounded font-mono text-center"
                    data-testid="input-legacy-dias"
                  />
                </label>
                <Button
                  variant="outline"
                  onClick={() => eliminarLegacy(true)}
                  disabled={legacyLoading}
                  className="border-red-300 text-red-800"
                  data-testid="btn-legacy-simular-eliminar"
                >
                  Simular
                </Button>
                <Button
                  onClick={() => eliminarLegacy(false)}
                  disabled={legacyLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="btn-legacy-eliminar"
                >
                  Eliminar definitivamente
                </Button>
              </div>
              {legacyElimResult && (
                <div className="mt-3 p-2.5 rounded bg-white border border-red-200 text-sm" data-testid="legacy-elim-result">
                  <span className="text-red-900">
                    {legacyElimResult.dry_run ? "Simulación:" : "Ejecución:"}
                  </span>{" "}
                  <b>{legacyElimResult.candidatas}</b> candidata(s) ·{" "}
                  <b className="text-red-700">{legacyElimResult.eliminadas}</b> eliminada(s) ·{" "}
                  cutoff <b>{legacyElimResult.dias_desde_marcado}</b> día(s)
                </div>
              )}
            </div>
          </div>
        )}
      </Card>


      <Card className="p-4 border-green-100">
        <h3 className="font-display font-bold text-lg text-green-900 mb-3 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-orange-500" /> Historial de ejecuciones
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900">
              <tr>
                <th className="text-left p-2.5">Fecha</th>
                <th className="text-left p-2.5">Disparador</th>
                <th className="text-center p-2.5">Modo</th>
                <th className="text-center p-2.5">Encontradas</th>
                <th className="text-center p-2.5">Revertidas</th>
                <th className="text-right p-2.5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-b border-green-50" data-testid={`log-row-${l.id}`}>
                  <td className="p-2.5 text-xs">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2.5 text-xs">
                    {disparadorBadge(l.disparado_por === "scheduler" ? "scheduler" : "manual")}
                    <span className="ml-2 text-slate-600">{l.disparado_por}</span>
                  </td>
                  <td className="p-2.5 text-center">
                    {l.dry_run
                      ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Detección</Badge>
                      : <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Reparación</Badge>}
                  </td>
                  <td className="p-2.5 text-center font-mono">{l.encontradas}</td>
                  <td className="p-2.5 text-center font-mono text-emerald-700 font-bold">{l.revertidas}</td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => verDetalle(l.id)} title="Ver detalle" data-testid={`ver-log-${l.id}`}>
                      <Eye className="w-4 h-4 text-green-700" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!logs.length && (
                <tr><td colSpan="6" className="p-8 text-center text-slate-400">Sin ejecuciones registradas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detalle log */}
      <Dialog open={!!detalleLog} onOpenChange={o => !o && setDetalleLog(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del log de mantenimiento</DialogTitle>
            <DialogDescription>
              {detalleLog?.timestamp?.slice(0, 16).replace("T", " ")} · {detalleLog?.disparado_por} · {detalleLog?.dry_run ? "Detección" : "Reparación"}
            </DialogDescription>
          </DialogHeader>
          {detalleLog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Encontradas</p>
                  <p className="text-2xl font-display font-extrabold text-slate-900">{detalleLog.encontradas}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Revertidas</p>
                  <p className="text-2xl font-display font-extrabold text-emerald-800">{detalleLog.revertidas}</p>
                </div>
              </div>

              {detalleLog.acciones?.length > 0 ? (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Declaraciones afectadas</p>
                  <div className="max-h-96 overflow-y-auto border rounded-lg border-green-100">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50 text-green-900 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Planilla</th>
                          <th className="text-left p-2">Contribuyente</th>
                          <th className="text-left p-2">Períodos</th>
                          <th className="text-left p-2">Pago referenciado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalleLog.acciones.map((a, i) => (
                          <tr key={i} className="border-b border-green-50">
                            <td className="p-2 font-mono text-xs">{a.nro_planilla}</td>
                            <td className="p-2 text-xs">{a.contribuyente_id?.slice(0, 8)}...</td>
                            <td className="p-2 text-xs">
                              {a.periodos_cubiertos?.length
                                ? a.periodos_cubiertos.map(labelPeriodo).join(", ")
                                : (a.mes_anio_declaracion ? labelPeriodo(a.mes_anio_declaracion) : "-")}
                            </td>
                            <td className="p-2 text-xs text-rose-700 font-mono">{a.pago_id_referenciado || "(sin pago_id)"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic text-center py-6">Sin acciones para esta ejecución.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: detalle sanity permisos */}
      <Dialog open={!!permDetalle} onOpenChange={(v) => !v && setPermDetalle(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              Detalle · {permDetalle?.nombre}
            </DialogTitle>
            <DialogDescription>
              {permDetalle?.usuarios_activos} usuario(s) activo(s) · {permDetalle?.total_permisos} permiso(s)
            </DialogDescription>
          </DialogHeader>
          {permDetalle && (
            <div className="space-y-3">
              {permDetalle.findings.map((f, i) => (
                <div key={i} className="p-3 rounded-lg bg-amber-50/60 border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-amber-700" />
                    <span className="font-mono text-sm text-amber-900 font-bold">Tiene: {f.trigger}</span>
                  </div>
                  <p className="text-xs text-amber-800/90 mb-2">{f.porque}</p>
                  <div className="flex flex-wrap gap-1">
                    {f.faltantes.map(p => (
                      <span key={p} className="inline-block bg-red-100 text-red-800 font-mono text-xs px-2 py-0.5 rounded">
                        Falta: {p}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {permDetalle.findings.length === 0 && (
                <p className="text-emerald-700 text-sm py-6 text-center">Sin incoherencias detectadas ✓</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: detalle grupo duplicadas */}
      <Dialog open={!!dupDetalle} onOpenChange={(v) => !v && setDupDetalle(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-amber-600" />
              Duplicados de {dupDetalle?.contribuyente_nombre}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {dupDetalle?.documento}
            </DialogDescription>
          </DialogHeader>
          {dupDetalle && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1.5">
                  ✓ Planilla(s) conservada(s)
                </p>
                <div className="border border-emerald-200 rounded-lg divide-y divide-emerald-100 bg-emerald-50/40">
                  {dupDetalle.planillas_conservadas.map(p => (
                    <div key={p.id} className="p-2.5 flex items-center justify-between text-sm">
                      <div>
                        <span className="font-mono font-bold text-emerald-800">{p.nro_planilla}</span>
                        <Badge className="ml-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{p.estatus}</Badge>
                        <span className="ml-2 text-slate-600 text-xs">
                          {(p.periodos_cubiertos || []).map(labelPeriodo).join(", ") || "—"}
                        </span>
                      </div>
                      <span className="text-slate-700 font-semibold">Bs. {(p.total || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-red-700 mb-1.5">
                  ✕ Planilla(s) que serán anuladas
                </p>
                <div className="border border-red-200 rounded-lg divide-y divide-red-100 bg-red-50/40">
                  {dupDetalle.planillas_a_anular.map(p => (
                    <div key={p.id} className="p-2.5 text-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono font-bold text-red-800">{p.nro_planilla}</span>
                          <Badge className="ml-2 bg-red-100 text-red-800 hover:bg-red-100">{p.estatus}</Badge>
                          <span className="ml-2 text-slate-600 text-xs">
                            {(p.periodos_cubiertos || []).map(labelPeriodo).join(", ") || "—"}
                          </span>
                        </div>
                        <span className="text-slate-700 font-semibold">Bs. {(p.total || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</span>
                      </div>
                      <p className="mt-1 text-xs text-red-700/90">{p.razon}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
